import React, { useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, OverlayTrigger, Row, Spinner, Table, Tooltip } from "react-bootstrap";
import { Check, Copy } from "react-feather";
import { apiErrorMessage, useGet, usePatch, usePost } from "../../api";
import { EmptyState, PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { usePermissions } from "../../hooks";
import {
  ISmartShoppingAccessCode,
  ISmartShoppingAccessCodeInput,
  ISmartShoppingAccessCodeUpdate,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";

const withHint = (hint: string, node: React.ReactElement) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{hint}</Tooltip>}>
    <span className="d-inline-block">{node}</span>
  </OverlayTrigger>
);

const CopyCode: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);
  return withHint(
    "Copy the code to share it",
    <Button
      size="sm"
      variant="link"
      className="p-0 font-monospace text-decoration-none"
      onClick={async () => {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {code} {copied ? <Check size={12} /> : <Copy size={12} />}
    </Button>
  );
};

const isExpired = (c: ISmartShoppingAccessCode) => !!c.expiresAt && new Date(c.expiresAt).getTime() < Date.now();

export const SmartShoppingAccessCodes: React.FC = () => {
  const { can, isAdmin } = usePermissions();
  const canCreate = isAdmin || can(PermissionAction.CreateAny, PermissionResource.SmartShopping);
  const canUpdate = isAdmin || can(PermissionAction.UpdateAny, PermissionResource.SmartShopping);
  const { data, loading, error, refetch } = useGet<ISmartShoppingAccessCode[]>("smart-shopping/access-codes");
  const { execute: create, loading: creating } = usePost<ISmartShoppingAccessCode, ISmartShoppingAccessCodeInput>();
  const { execute: patch } = usePatch<ISmartShoppingAccessCode, ISmartShoppingAccessCodeUpdate>();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "", dailyTurnLimit: 100, expiresAt: "", code: "" });
  const [notice, setNotice] = useState<{ variant: string; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const created = await create("smart-shopping/access-codes", {
        label: form.label.trim(),
        dailyTurnLimit: Number(form.dailyTurnLimit) || 100,
        expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T23:59:59`).toISOString() : null,
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
      });
      setNotice({ variant: "success", text: `Created ${created.code} for “${created.label}”.` });
      setForm({ label: "", dailyTurnLimit: 100, expiresAt: "", code: "" });
      setShowForm(false);
      refetch().catch(() => undefined);
    } catch (err) {
      setNotice({ variant: "danger", text: apiErrorMessage(err) });
    }
  };

  const update = async (c: ISmartShoppingAccessCode, body: ISmartShoppingAccessCodeUpdate, done: string) => {
    setBusyId(c._id);
    try {
      await patch(`smart-shopping/access-codes/${c._id}`, body);
      setNotice({ variant: "success", text: done });
      refetch().catch(() => undefined);
    } catch (err) {
      setNotice({ variant: "danger", text: apiErrorMessage(err) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Smart Shopping invite codes"
        description="People enter one of these codes in the extension to try the shopping agent. Each code has its own daily request limit and can be paused at any time."
      />
      <PageToolbar>
        {canCreate && (
          <ToolbarButton label="New invite code" icon="bi-plus-lg" variant="primary" onClick={() => setShowForm((s) => !s)} hint="Create a code to share with a partner, a team or a single tester" />
        )}
        <ToolbarButton label="Sessions" icon="bi-chat-square-text" to="/smart-shopping/sessions" hint="Review what people did with the extension" />
      </PageToolbar>

      {notice && (
        <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      {showForm && (
        <Card className="mb-3">
          <Card.Body>
            <Form onSubmit={submit}>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Label className="small fw-semibold mb-1">Who it's for</Form.Label>
                  <Form.Control size="sm" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Muse pilot" />
                  <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Shown on sessions, so you can tell testers apart.
                  </Form.Text>
                </Col>
                <Col md={2}>
                  <Form.Label className="small fw-semibold mb-1">Requests per day</Form.Label>
                  <Form.Control size="sm" type="number" min={1} max={5000} value={form.dailyTurnLimit} onChange={(e) => setForm({ ...form, dailyTurnLimit: Number(e.target.value) })} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small fw-semibold mb-1">Expires</Form.Label>
                  <Form.Control size="sm" type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small fw-semibold mb-1">Custom code</Form.Label>
                  <Form.Control size="sm" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Generated" />
                </Col>
                <Col md={2}>
                  <Button type="submit" size="sm" disabled={creating || !form.label.trim()}>
                    {creating ? <Spinner animation="border" size="sm" /> : "Create code"}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {data && data.length === 0 && (
        <EmptyState title="No invite codes yet" message="Create one to let people try Kindred Smart Shopping." icon="bi-key" />
      )}
      {data && data.length > 0 && (
        <Table hover responsive size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Code</th>
              <th>For</th>
              <th>Status</th>
              <th className="text-end">Daily limit</th>
              <th className="text-end">Requests so far</th>
              <th>Last used</th>
              <th>Expires</th>
              <th>Created by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((c) => {
              const expired = isExpired(c);
              return (
                <tr key={c._id}>
                  <td>
                    <CopyCode code={c.code} />
                  </td>
                  <td>{c.label}</td>
                  <td>
                    <Badge bg={!c.active ? "secondary" : expired ? "warning" : "success"} text={expired && c.active ? "dark" : undefined}>
                      {!c.active ? "Paused" : expired ? "Expired" : "Active"}
                    </Badge>
                  </td>
                  <td className="text-end">
                    {canUpdate ? (
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        max={5000}
                        defaultValue={c.dailyTurnLimit}
                        style={{ width: 90, marginLeft: "auto" }}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (v && v !== c.dailyTurnLimit) void update(c, { dailyTurnLimit: v }, `${c.code} now allows ${v} requests a day.`);
                        }}
                        title="Requests this code can make per day (UTC). Saves when you leave the field."
                      />
                    ) : (
                      c.dailyTurnLimit
                    )}
                  </td>
                  <td className="text-end">{c.totalTurns.toLocaleString()}</td>
                  <td className="small">{c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : <span className="text-muted">Never</span>}</td>
                  <td className="small">{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : <span className="text-muted">No expiry</span>}</td>
                  <td className="small text-muted">{c.createdBy ?? "—"}</td>
                  <td className="text-end">
                    {canUpdate &&
                      withHint(
                        c.active ? "Stop this code working straight away; open sessions end at their next request" : "Let this code be used again",
                        <Button
                          size="sm"
                          variant={c.active ? "outline-danger" : "outline-success"}
                          disabled={busyId === c._id}
                          onClick={() => update(c, { active: !c.active }, c.active ? `${c.code} is paused.` : `${c.code} is active again.`)}
                        >
                          {c.active ? "Pause" : "Activate"}
                        </Button>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
};
